import type { AppDispatch } from '@/store';
import type { CryptoService } from '@/services/abstract/poc/crypto.service.interface';
import type { UserService } from '@/services/abstract/UserService';
import type { Logger } from '@/types/Logger';
import { setDecapsKey } from '@/store/slices/auth/authSlice';
import { bytesToHex, hexToBytes } from '@/utils/hex.utils';

interface EnsureDecapsPrivateKeyArgs {
  /** Cached decrypted private key as hex (`state.auth.decapsKey`); empty if not cached. */
  decapsKeyHex: string;
  /** User key-encryption key as hex (`state.auth.ukek`); required on a cache miss. */
  ukekHex: string;
  userId: string;
  dispatch: AppDispatch;
  userService: UserService;
  cryptoService: CryptoService;
  log: Logger;
}

/**
 * Resolves the current user's decrypted ML-KEM private (decaps) key: returns it
 * from the Redux cache (`auth.decapsKey`) when present, otherwise fetches the
 * encrypted key, decrypts it with the UKEK, caches the result via `setDecapsKey`,
 * and returns it. Shared by the download and file-key-facilitation flows.
 *
 * This is the seam the planned CryptoSessionVault (Phase 3) will take over — the
 * single place the private key is loaded, decrypted, and cached.
 */
export async function ensureDecapsPrivateKey({
  decapsKeyHex,
  ukekHex,
  userId,
  dispatch,
  userService,
  cryptoService,
  log,
}: EnsureDecapsPrivateKeyArgs): Promise<Uint8Array> {
  if (decapsKeyHex.length > 0) {
    log.debug('Using cached private key');
    return hexToBytes(decapsKeyHex);
  }

  log.debug('No cached key, fetching and decrypting...');
  if (ukekHex.length === 0) {
    throw new Error('User encryption key (UKEK) not available for private key decryption');
  }

  const { decapsKey: encryptedPrivateKeyHex } = await userService.getDecapsKey(userId);
  const privateKey = await cryptoService.decryptPrivateKey(
    encryptedPrivateKeyHex,
    hexToBytes(ukekHex)
  );

  dispatch(setDecapsKey(bytesToHex(privateKey)));
  log.debug('Private key decrypted and cached');
  return privateKey;
}
